import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import {
  isEmpty,
  isStrongEnoughPassword,
  isValidEmail,
  isValidPhoneNumber,
} from "../utils/validators";
import * as authService from "../services/authService";

const Register = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [serverMessage, setServerMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const nextErrors = {};

    if (isEmpty(form.firstName)) nextErrors.firstName = "Please enter your first name.";
    if (isEmpty(form.lastName)) nextErrors.lastName = "Please enter your last name.";
    if (!isValidEmail(form.email)) nextErrors.email = "Please enter a valid email address.";
    if (!isValidPhoneNumber(form.phoneNumber)) {
      nextErrors.phoneNumber = "Please enter a valid phone number.";
    }
    if (!isStrongEnoughPassword(form.password)) {
      nextErrors.password = "Your password must be at least 8 characters.";
    }
    if (form.confirmPassword !== form.password) {
      nextErrors.confirmPassword = "Your passwords do not match.";
    }

    return nextErrors;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setServerMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validateForm();
    setErrors(nextErrors);
    setServerMessage("");

    if (Object.keys(nextErrors).length > 0) return;

    try {
      setLoading(true);

      const email = form.email.trim();

      const data = await authService.register({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email,
        password: form.password,
        phoneNumber: form.phoneNumber.trim(),
      });

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Account creation failed.");
      }

      navigate("/action-status", {
        replace: true,
        state: {
          status: "email",
          eyebrow: "Account Created",
          title: "Please verify your email",
          message:
            "Your account has been created successfully. Please verify your email before signing in.",
          note:
            data?.message ||
            "A verification email has been sent to your inbox. Open the email and click the verification link to activate your account.",
          details: [
            {
              label: "Email Address",
              value: email,
            },
            {
              label: "Next Step",
              value: "Open your inbox and click the verification link.",
            },
          ],
          actions: [
            {
              label: "Go to Sign In",
              to: "/login",
              variant: "primary",
            },
            {
              label: "Back to Home",
              to: "/",
              variant: "secondary",
            },
          ],
        },
      });
    } catch (err) {
      setServerMessage(err?.message || "We could not create your account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-section">
      <PageContainer width="md">
        <div className="mx-auto max-w-2xl content-card-lg p-8">
          <SectionTitle
            eyebrow="Create Your Account"
            title="Create an account for a smoother booking experience"
            subtitle="Save your details, review your bookings, and make future appointments faster and easier."
            className="mb-8"
          />

          {serverMessage ? (
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-700">{serverMessage}</p>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="form-grid two">
              <Input
                label="First Name"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                error={errors.firstName}
                required
                placeholder="Enter your first name"
              />

              <Input
                label="Last Name"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                error={errors.lastName}
                required
                placeholder="Enter your last name"
              />
            </div>

            <div className="form-grid two">
              <Input
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                error={errors.email}
                required
                placeholder="Enter your email address"
              />

              <Input
                label="Phone Number"
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleChange}
                error={errors.phoneNumber}
                required
                placeholder="Enter your phone number"
              />
            </div>

            <div className="form-grid two">
              <Input
                label="Password"
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                error={errors.password}
                required
                hint="Use at least 8 characters."
                showPasswordToggle
              />

              <Input
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={handleChange}
                error={errors.confirmPassword}
                required
                showPasswordToggle
              />
            </div>

            <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
              Create My Account
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-warm">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-henna hover:underline">
              Sign in here
            </Link>
          </p>
        </div>
      </PageContainer>
    </div>
  );
};

export default Register;